import { defineStore, storeToRefs } from "pinia";
import { ref, computed, watch } from "vue";
import { useUserStore } from "@/stores/userStore";
import {
    fetchModalities,
    fetchAvailableModalities,
    updateModality,
} from "@/api/modality";

export interface ModalityOption {
    value: string;
    title: string;
    description: string;
    icon: string;
    // Category (taxonomy) slugs this modality maps to. Course filtering matches
    // these against a course's categories — the modality's own `value` slug is
    // not assumed to be a course category slug.
    taxonomySlugs: string[];
}

export const useGlobalFilterStore = defineStore("globalFilter", () => {
    const userStore = useUserStore();
    const { userObj, loaded } = storeToRefs(userStore);

    const modality = ref<string | null>(null);
    const allModalities = ref<ModalityOption[]>([]);
    const availableModalityOptions = ref<ModalityOption[]>([]);
    const availableModalitiesLoaded = ref(false);

    // Fetch the full modality list (used to resolve the active modality's title,
    // even if it falls outside the user's available set).
    fetchModalities().then((options) => {
        allModalities.value = options;
    });

    // Fetch the modalities available to the current user for the Global Filter.
    fetchAvailableModalities().then((options) => {
        availableModalityOptions.value = options;
        availableModalitiesLoaded.value = true;
    });

    const availableModalitySlugs = computed<string[]>(() =>
        availableModalityOptions.value.map((opt) => opt.value)
    );

    async function setFilter(newValue: string): Promise<void> {
        await updateModality(newValue);
        modality.value = newValue;
    }

    // Initialize from the user object once auth-status AND available modalities are loaded.
    // If the user has no active modality but only one option is available, auto-select it.
    watch(
        [loaded, userObj, availableModalitiesLoaded],
        ([isLoaded, user, modReady]) => {
            if (isLoaded && user && modReady) {
                const available = availableModalitySlugs.value;
                const active = user.active_modality ?? null;

                if (active && available.includes(active)) {
                    modality.value = active;
                } else if (available.length === 1) {
                    setFilter(available[0]);
                } else {
                    modality.value = null;
                }
            }
        },
        { immediate: true }
    );

    const isSet = computed(() => modality.value !== null);
    const value = computed(() => modality.value);

    // Category slugs the active modality maps to, used to scope the course
    // catalogue. Resolved from the full modality list (the active modality may
    // sit outside the user's available set) falling back to the available set.
    const activeTaxonomySlugs = computed<string[]>(() => {
        if (!modality.value) return [];
        const option =
            allModalities.value.find((o) => o.value === modality.value) ??
            availableModalityOptions.value.find(
                (o) => o.value === modality.value
            );
        return option?.taxonomySlugs ?? [];
    });

    return {
        isSet,
        value,
        setFilter,
        availableModalitySlugs,
        availableModalityOptions,
        allModalities,
        activeTaxonomySlugs,
    };
});
